<template>
  <div>
    <b-sidebar
      id="sidebar-business-unit"
      sidebar-class="sidebar-lg"
      :visible="businessUnitSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="(val) => mutateBusinessUnitSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4 v-if="isEdit">Editar unidade de negócio</h4>
          <h4 v-else>Nova unidade de negócio</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <b-form class="p-2" @submit.prevent="onSubmit">
          <b-row>
            <b-col cols="12">
              <b-form-group label="Nome" label-for="business-unit-name">
                <b-form-input
                  id="business-unit-name"
                  v-model="name"
                  :class="{ 'is-invalid': v$.name.$error }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.name.required.$invalid">
                    Você deve informar o nome da unidade de negócio
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="mt-1">
              <b-form-group label="Responsável" label-for="business-unit-consultant">
                <v-select
                  id="business-unit-consultant"
                  v-model="consultantId"
                  :options="consultants"
                  :loading="loading.consultants"
                  :reduce="(consultant) => consultant.id"
                  label="name"
                  :class="getSelectErrorClass(v$.consultantId.$error)"
                >
                  <template #no-options>
                    Sem resultados
                  </template>
                </v-select>
                <div class="invalid-feedback">
                  <span v-if="v$.consultantId.required.$invalid">
                    Você deve selecionar o responsável
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="mt-1">
              <b-form-group label="Departamento" label-for="business-unit-department">
                <v-select
                  id="business-unit-department"
                  v-model="departmentId"
                  :options="departments"
                  :loading="loading.departments"
                  :reduce="(department) => department.id"
                  label="name"
                >
                  <template #no-options>
                    Sem resultados
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="mt-1">
              <b-form-group label="URL de destino" label-for="business-unit-landing-url">
                <b-form-input
                  id="business-unit-landing-url"
                  v-model="landingUrl"
                  :class="{ 'is-invalid': v$.landingUrl.$error }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.landingUrl.url.$invalid || v$.landingUrl.httpProtocol.$invalid">
                    Você deve informar uma URL válida, iniciando com http:// ou https://
                  </span>
                  <span v-if="v$.landingUrl.maxLength.$invalid">
                    A URL de destino deve ter no máximo 2048 caracteres
                  </span>
                </div>
                <small class="form-text text-muted">
                  Página de cadastro da unidade (mantida pela Inovação) para onde o link da campanha redireciona. Use {campaign_token} para posicionar o token da campanha; sem ele, o token vai no parâmetro campaign_token.
                </small>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="d-flex mt-2 justify-content-between">
            <b-button
              :disabled="saving"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="outline-primary"
              @click="hide"
            >
              Voltar
            </b-button>
            <b-button
              :disabled="saving"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              type="submit"
            >
              {{ saving ? "Salvando..." : "Salvar" }}
            </b-button>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BButton,
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BCol,
  BRow,
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import useVuelidate from "@vuelidate/core";
import { required, url, maxLength } from "@vuelidate/validators";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as sharedTypes from "@/modules/shared/store/types";
import { getVueSelectErrorClass } from "@/helpers/validators";
import * as types from "../store/types";

export default {
  components: {
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BCol,
    BRow,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: {
        consultants: false,
        departments: false,
      },
      name: undefined,
      consultantId: undefined,
      departmentId: undefined,
      landingUrl: undefined,
      saving: false,
    };
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  validations() {
    return {
      name: { required },
      consultantId: { required },
      landingUrl: {
        url,
        httpProtocol(value) {
          return !value || /^https?:\/\//i.test(value);
        },
        maxLength: maxLength(2048),
      },
    };
  },
  computed: {
    ...mapGetters({
      businessUnitSidebar: types.BUSINESS_UNIT_SIDEBAR,
      consultants: sharedTypes.CONSULTANTS,
      departments: sharedTypes.DEPARTMENTS,
    }),
    isEdit() {
      return !!this.businessUnitSidebar.id;
    },
  },
  methods: {
    ...mapMutations({
      mutateBusinessUnitSidebar: types.MUTATE_BUSINESS_UNIT_SIDEBAR,
    }),
    ...mapActions({
      getBusinessUnit: types.GET_BUSINESS_UNIT,
      saveBusinessUnit: types.SAVE_BUSINESS_UNIT,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getDepartments: sharedTypes.GET_DEPARTMENTS,
    }),
    onShow() {
      this.loading.consultants = true;
      this.getConsultants()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.consultants = false;
        });

      this.loading.departments = true;
      this.getDepartments()
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao carregar os departamentos para seleção. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading.departments = false;
        });

      if (this.isEdit) {
        this.getBusinessUnit(this.businessUnitSidebar.id)
          .then((resp) => {
            this.name = resp.data.name;
            this.consultantId = resp.data.consultant_id;
            this.departmentId = resp.data.department_id;
            this.landingUrl = resp.data.landing_url;
          })
          .catch(() => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao carregar a unidade de negócio para edição. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          });
      }
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      this.saving = true;
      this.saveBusinessUnit({
        id: this.businessUnitSidebar.id,
        name: this.name,
        consultant_id: this.consultantId,
        department_id: this.departmentId || null,
        landing_url: this.landingUrl || null,
      })
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "Unidade de negócio salva com sucesso!",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.mutateBusinessUnitSidebar({ visible: false });
          this.businessUnitSidebar.saveAction();
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao salvar a unidade de negócio. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },
    clear() {
      this.name = undefined;
      this.consultantId = undefined;
      this.departmentId = undefined;
      this.landingUrl = undefined;
      this.v$.$reset();
    },
  },
};
</script>
